import { useEffect, useRef, useState } from "react";
import SpeakButton from "@/components/SpeakButton";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { ArrowLeft, Send } from "lucide-react";
import { makeApi, BACKEND_URL } from "@/lib/api";
import { useT } from "@/i18n";
import { LumiMascot, SparkMascot } from "@/components/Mascots";

export default function ChatPage() {
  const { mascot = "lumi" } = useParams();
  const [searchParams] = useSearchParams();
  const isLumi = mascot === "lumi";
  const { getToken } = useAuth();
  const { t, lang } = useT();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => searchParams.get("q") || "");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const api = makeApi(getToken);
    api
      .get(`/chat/history`, { params: { mascot } })
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => setMessages([]));
  }, [mascot, getToken]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    setInput("");
    const userMsg = { role: "user", text, ts: new Date().toISOString() };
    const botMsg = { role: "assistant", text: "", ts: new Date().toISOString(), streaming: true };
    setMessages((m) => [...m, userMsg, botMsg]);
    setStreaming(true);

    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text, mascot, language: lang }),
      });
      if (!res.ok) {
        const detail = await res.json().then((j) => j.detail).catch(() => "");
        const limit = detail === "daily_text_limit";
        const msg = limit
          ? (lang === "es" ? "¡Hoy ya conversamos muchísimo! Volvamos a hablar mañana." : "We talked a lot today! Let's chat again tomorrow.")
          : detail === "too_many_requests"
            ? (lang === "es" ? "¡Uy, vas muy rápido! Espera un momentito y vuelve a intentar." : "Whoa, too fast! Wait a moment and try again.")
            : "⚠️ Error";
        setMessages((m) => {
          const n = [...m];
          n[n.length - 1] = { ...n[n.length - 1], text: msg, streaming: false, error: true };
          return n;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          try {
            const json = JSON.parse(p.slice(5).trim());
            if (json.delta) {
              setMessages((m) => {
                const n = [...m];
                n[n.length - 1] = { ...n[n.length - 1], text: n[n.length - 1].text + json.delta };
                return n;
              });
            }
            if (json.replace) {
              setMessages((m) => {
                const n = [...m];
                n[n.length - 1] = { ...n[n.length - 1], text: json.replace };
                return n;
              });
            }
            if (json.done) {
              setMessages((m) => {
                const n = [...m];
                n[n.length - 1] = { ...n[n.length - 1], streaming: false };
                return n;
              });
            }
            if (json.error) {
              setMessages((m) => {
                const n = [...m];
                n[n.length - 1] = { ...n[n.length - 1], text: "⚠️ " + json.error, streaming: false, error: true };
                return n;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const n = [...m];
        n[n.length - 1] = { ...n[n.length - 1], text: "⚠️ Error", streaming: false, error: true };
        return n;
      });
    } finally {
      setStreaming(false);
    }
  };

  const starters = isLumi ? t.chat.startersLumi : t.chat.startersSpark;
  const bg = isLumi ? "bg-yellow-100" : "bg-sky-100";
  const bubbleBot = isLumi ? "bg-yellow-300" : "bg-sky-300";

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex flex-col h-[calc(100vh-80px)]">
        <Link to="/dashboard" className="inline-flex items-center gap-2 font-bold text-slate-700 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> {t.nav.dashboard}
        </Link>

        <div className="mt-4 flex items-center gap-4">
          {isLumi ? <LumiMascot size={70} /> : <SparkMascot size={70} />}
          <div>
            <h1 className="font-fredoka text-3xl font-bold text-slate-900">
              {isLumi ? t.chat.withLumi : t.chat.withSpark}
            </h1>
          </div>
        </div>

        <div className={`mt-6 flex-1 ${bg} border-4 border-slate-900 chunky-shadow rounded-3xl p-5 overflow-y-auto`} data-testid="chat-window">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <p className="text-slate-700 text-center">
                {lang === "es" ? "¡Prueba una de estas preguntas!" : "Try one of these!"}
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {starters.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="press-btn bg-white border-2 border-slate-900 chunky-shadow-sm rounded-full px-4 py-2 text-sm font-bold"
                    data-testid={`chat-starter-${s.slice(0, 10)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} mb-3`}>
              <div
                className={`max-w-[80%] border-2 border-slate-900 rounded-2xl px-4 py-2.5 ${
                  m.role === "user"
                    ? "bg-white rounded-tr-none"
                    : `${bubbleBot} rounded-tl-none`
                }`}
              >
                <p className="whitespace-pre-wrap text-slate-900">{m.text}</p>
                {m.role !== "user" && !m.streaming && !m.error && m.text && (
                  <div className="mt-1.5"><SpeakButton compact mascot={isLumi ? "lumi" : "spark"} text={m.text} testId={`chat-speak-${i}`} /></div>
                )}
                {m.streaming && (
                  <div className="flex gap-1 mt-1">
                    <span className="w-1.5 h-1.5 bg-slate-900 rounded-full typing-dot" />
                    <span className="w-1.5 h-1.5 bg-slate-900 rounded-full typing-dot" />
                    <span className="w-1.5 h-1.5 bg-slate-900 rounded-full typing-dot" />
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
          className="mt-4 flex gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t.chat.placeholder}
            className="flex-1 bg-white border-2 border-slate-900 chunky-shadow-sm rounded-full px-5 py-3 font-nunito focus:outline-none focus:ring-4 focus:ring-sky-300"
            data-testid="chat-input"
            disabled={streaming}
          />
          <button
            type="submit"
            disabled={streaming || !input.trim()}
            className="press-btn bg-pink-500 text-white border-2 border-slate-900 chunky-shadow-sm rounded-full px-5 py-3 font-bold disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" /> {t.chat.send}
          </button>
        </form>
      </div>
    </div>
  );
}
